import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, Plus, Pencil, Trash2,
  RefreshCw, Loader2, ArrowLeft, AlertTriangle, ChevronDown, ChevronUp,
  BarChart3, Shield, Coins, Bell, Send, Settings, CheckCircle2, XCircle,
  History, ArrowDownLeft, ArrowUpRight, PieChart as PieChartIcon, Star, Wallet
} from 'lucide-react';
import { InvestorProfilePanel, PortfolioCharts, DividendPanel, ScreeningPanel } from './BRDashboardPanels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASSET_TYPES = [
  { value: 'fii', label: 'FII' },
  { value: 'etf', label: 'ETF' },
  { value: 'stock', label: 'Stock' },
  { value: 'bond', label: 'Bond' },
  { value: 'cri', label: 'CRI/CRA' },
  { value: 'trust', label: 'Trust' },
];

const BR_TICKERS = [
  // FIIs - CRI/CRA
  { ticker: 'MXRF11', type: 'fii', sector: 'CRI/CRA', name: 'Maxi Renda' },
  { ticker: 'KNCR11', type: 'fii', sector: 'CRI/CRA', name: 'Kinea Rendimentos' },
  { ticker: 'KNIP11', type: 'fii', sector: 'CRI/CRA', name: 'Kinea Índices de Preços' },
  { ticker: 'RBRR11', type: 'fii', sector: 'CRI/CRA', name: 'RBR Rendimento High Grade' },
  { ticker: 'VGIR11', type: 'fii', sector: 'CRI/CRA', name: 'Valora RE III' },
  { ticker: 'SNCI11', type: 'fii', sector: 'CRI/CRA', name: 'Suno Recebíveis' },
  { ticker: 'CACR11', type: 'fii', sector: 'CRI/CRA', name: 'Cartesia Recebíveis' },
  { ticker: 'MCCI11', type: 'fii', sector: 'CRI/CRA', name: 'Mauá Capital Recebíveis' },
  { ticker: 'BTCI11', type: 'fii', sector: 'CRI/CRA', name: 'BTG Pactual Crédito' },
  { ticker: 'HGCR11', type: 'fii', sector: 'CRI/CRA', name: 'CSHG Recebíveis' },
  { ticker: 'IRDM11', type: 'fii', sector: 'CRI/CRA', name: 'Iridium Recebíveis' },
  { ticker: 'CPTS11', type: 'fii', sector: 'CRI/CRA', name: 'Capitânia Securities II' },
  // FIIs - Logistics
  { ticker: 'HGLG11', type: 'fii', sector: 'Logistics', name: 'CSHG Logística' },
  { ticker: 'XPLG11', type: 'fii', sector: 'Logistics', name: 'XP Log' },
  { ticker: 'BTLG11', type: 'fii', sector: 'Logistics', name: 'BTG Pactual Logística' },
  { ticker: 'VILG11', type: 'fii', sector: 'Logistics', name: 'Vinci Logística' },
  { ticker: 'BRCO11', type: 'fii', sector: 'Logistics', name: 'Bresco Logística' },
  // FIIs - Shopping
  { ticker: 'XPML11', type: 'fii', sector: 'Shopping', name: 'XP Malls' },
  { ticker: 'VISC11', type: 'fii', sector: 'Shopping', name: 'Vinci Shopping Centers' },
  { ticker: 'HSML11', type: 'fii', sector: 'Shopping', name: 'HSI Malls' },
  { ticker: 'MALL11', type: 'fii', sector: 'Shopping', name: 'Malls Brasil Plural' },
  // FIIs - Office / Corporate
  { ticker: 'KORE11', type: 'fii', sector: 'Office', name: 'Kinea Oportunidades RE' },
  { ticker: 'BRCR11', type: 'fii', sector: 'Office', name: 'BTG Pactual Corporate Office' },
  { ticker: 'HGRE11', type: 'fii', sector: 'Office', name: 'CSHG Real Estate' },
  { ticker: 'PVBI11', type: 'fii', sector: 'Office', name: 'VBI Prime Properties' },
  // FIIs - FOF
  { ticker: 'SNFF11', type: 'fii', sector: 'FoF', name: 'Suno FoF' },
  { ticker: 'KFOF11', type: 'fii', sector: 'FoF', name: 'Kinea FoF' },
  { ticker: 'BCFF11', type: 'fii', sector: 'FoF', name: 'BTG Pactual FoF' },
  { ticker: 'HFOF11', type: 'fii', sector: 'FoF', name: 'Hedge Top FOFII' },
  // FIIs - Others
  { ticker: 'RBRF11', type: 'fii', sector: 'Hybrid', name: 'RBR Alpha Multiestratégia' },
  { ticker: 'RECR11', type: 'fii', sector: 'CRI/CRA', name: 'REC Recebíveis' },
  { ticker: 'VRTA11', type: 'fii', sector: 'CRI/CRA', name: 'Fator Veritá' },
  { ticker: 'HABT11', type: 'fii', sector: 'CRI/CRA', name: 'Habitat II' },
  { ticker: 'TGAR11', type: 'fii', sector: 'Development', name: 'TG Ativo Real' },
  { ticker: 'RZTR11', type: 'fii', sector: 'Agro', name: 'Riza Terrax' },
  // ETFs
  { ticker: 'BOVA11', type: 'etf', sector: 'Index', name: 'iShares Ibovespa' },
  { ticker: 'IVVB11', type: 'etf', sector: 'International', name: 'iShares S&P 500 BRL' },
  { ticker: 'SMAL11', type: 'etf', sector: 'Small Cap', name: 'iShares Small Cap' },
  { ticker: 'HASH11', type: 'etf', sector: 'Crypto', name: 'Hashdex Nasdaq Crypto' },
  { ticker: 'DIVO11', type: 'etf', sector: 'Dividends', name: 'It Now IDIV' },
  // Stocks
  { ticker: 'PETR4', type: 'stock', sector: 'Energy', name: 'Petrobras PN' },
  { ticker: 'VALE3', type: 'stock', sector: 'Mining', name: 'Vale ON' },
  { ticker: 'ITUB4', type: 'stock', sector: 'Banking', name: 'Itaú Unibanco PN' },
  { ticker: 'BBDC4', type: 'stock', sector: 'Banking', name: 'Bradesco PN' },
  { ticker: 'BBAS3', type: 'stock', sector: 'Banking', name: 'Banco do Brasil ON' },
  { ticker: 'ABEV3', type: 'stock', sector: 'Consumer', name: 'Ambev ON' },
  { ticker: 'WEGE3', type: 'stock', sector: 'Industrial', name: 'WEG ON' },
  { ticker: 'RENT3', type: 'stock', sector: 'Services', name: 'Localiza ON' },
  { ticker: 'ELET3', type: 'stock', sector: 'Utilities', name: 'Eletrobras ON' },
  { ticker: 'B3SA3', type: 'stock', sector: 'Financial', name: 'B3 ON' },
  { ticker: 'TAEE11', type: 'stock', sector: 'Utilities', name: 'Taesa Unit' },
  { ticker: 'BBSE3', type: 'stock', sector: 'Insurance', name: 'BB Seguridade ON' },
  { ticker: 'SUZB3', type: 'stock', sector: 'Paper/Pulp', name: 'Suzano ON' },
  { ticker: 'JBSS3', type: 'stock', sector: 'Food', name: 'JBS ON' },
  { ticker: 'MGLU3', type: 'stock', sector: 'Retail', name: 'Magazine Luiza ON' },
];

const formatBRL = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatJPY = (v) => `¥${Math.round(v || 0).toLocaleString('ja-JP')}`;
const formatPct = (v) => `${(v || 0) >= 0 ? '+' : ''}${(v || 0).toFixed(2)}%`;

const PnlBadge = ({ value }) => {
  if (!value && value !== 0) return null;
  const color = value >= 0 ? 'text-emerald-400' : 'text-red-400';
  const Icon = value >= 0 ? TrendingUp : TrendingDown;
  return <span className={`${color} font-mono text-sm flex items-center gap-1`}><Icon className="h-3 w-3" />{formatPct(value)}</span>;
};

const YieldBadge = ({ value }) => {
  if (!value) return <span className="text-zinc-600">-</span>;
  let color = 'text-zinc-400';
  if (value >= 10) color = 'text-emerald-400 font-bold';
  else if (value >= 7) color = 'text-emerald-300';
  else if (value >= 4) color = 'text-yellow-400';
  else color = 'text-red-400';
  return <span className={`${color} font-mono text-sm`}>{value.toFixed(2)}%</span>;
};

const RiskBadge = ({ score, factors }) => {
  let color = 'bg-emerald-500/20 text-emerald-400';
  let label = 'Low';
  if (score >= 50) { color = 'bg-red-500/20 text-red-400'; label = 'High'; }
  else if (score >= 25) { color = 'bg-amber-500/20 text-amber-400'; label = 'Med'; }
  return (
    <span className={`${color} px-2 py-0.5 rounded text-xs font-medium`} title={factors?.join(', ') || ''}>
      {score > 0 && <AlertTriangle className="h-3 w-3 inline mr-1" />}{label}
    </span>
  );
};

const BRInvestPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [txDetailTicker, setTxDetailTicker] = useState(null);
  const [txDetailData, setTxDetailData] = useState([]);
  const [txDetailTotal, setTxDetailTotal] = useState(0);
  const [txDetailLoading, setTxDetailLoading] = useState(false);
  const [txLoadingMore, setTxLoadingMore] = useState(false);
  const [txEditingId, setTxEditingId] = useState(null);
  const [txEditForm, setTxEditForm] = useState({});
  const [txAddMode, setTxAddMode] = useState(false);
  const [txAddForm, setTxAddForm] = useState({ transaction_type: 'buy', quantity: '', price: '', other_costs: '', purchase_date: new Date().toISOString().split('T')[0] });
  const [sortField, setSortField] = useState('annual_yield');
  const [sortDir, setSortDir] = useState('desc');
  const [form, setForm] = useState({ ticker: '', asset_type: 'stock', quantity: '1', avg_price: '', sector: '', notes: '', transaction_type: 'buy', purchase_date: new Date().toISOString().split('T')[0], other_costs: '' });
  const [tickerQuery, setTickerQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [b3Searching, setB3Searching] = useState(false);
  const [b3Result, setB3Result] = useState(null);
  const [masterTickers, setMasterTickers] = useState([]);
  const [b3Scanning, setB3Scanning] = useState(false);
  const [autoSearchTimer, setAutoSearchTimer] = useState(null);

  // Load master ticker list (from DB or fallback to static)
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/br/ticker-master`, { withCredentials: true })
      .then(r => { if (r.data?.length > 0) setMasterTickers(r.data); else setMasterTickers(BR_TICKERS); })
      .catch(() => setMasterTickers(BR_TICKERS));
  }, []);

  const reloadMaster = () => {
    axios.get(`${API_URL}/api/admin/br/ticker-master`, { withCredentials: true })
      .then(r => { if (r.data?.length > 0) setMasterTickers(r.data); })
      .catch(() => {});
  };

  const allTickers = masterTickers.length > 0 ? masterTickers : BR_TICKERS;

  const tickerSuggestions = tickerQuery.length > 0
    ? allTickers.filter(t =>
        (t.ticker || '').toLowerCase().includes(tickerQuery.toLowerCase()) ||
        (t.name || '').toLowerCase().includes(tickerQuery.toLowerCase())
      ).slice(0, 8)
    : [];

  const selectTicker = (item) => {
    setForm({ ...form, ticker: item.ticker, asset_type: item.type || item.asset_type, sector: item.sector || '' });
    setTickerQuery(item.ticker);
    setShowSuggestions(false);
    setB3Result(item.price ? item : null);
    // Auto-search B3 for full info (sector, price, type)
    searchB3(item.ticker);
  };

  const handleScanB3 = async () => {
    setB3Scanning(true);
    try {
      await axios.post(`${API_URL}/api/admin/br/scan-b3`, {}, { withCredentials: true });
      toast.success('B3 Sync started...');
      // Poll for status
      const poll = setInterval(async () => {
        try {
          const st = await axios.get(`${API_URL}/api/admin/br/scan-status`, { withCredentials: true });
          const d = st.data;
          if (!d.running) {
            clearInterval(poll);
            setB3Scanning(false);
            toast.success(`B3: ${d.valid} ${t('br.b3ScanComplete')}`);
            const r = await axios.get(`${API_URL}/api/admin/br/ticker-master`, { withCredentials: true });
            if (r.data?.length > 0) setMasterTickers(r.data);
          }
        } catch { clearInterval(poll); setB3Scanning(false); }
      }, 5000);
    } catch { toast.error('B3 scan failed'); setB3Scanning(false); }
  };

  const searchB3 = async (query) => {
    if (!query || query.length < 3) return;
    setB3Searching(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/br/search-ticker?q=${query}`, { withCredentials: true });
      const r = res.data;
      setForm(f => ({ ...f, ticker: r.ticker, asset_type: r.asset_type, sector: r.sector }));
      setTickerQuery(r.ticker);
      setB3Result(r);
      setShowSuggestions(false);
      toast.success(`B3: ${r.ticker} (${r.asset_type.toUpperCase()}) - ${r.name}`);
      reloadMaster();
    } catch {
      toast.error(t('br.b3NotFound'));
    } finally { setB3Searching(false); }
  };

  const totalValue = ((parseFloat(form.quantity) || 0) * (parseFloat(form.avg_price) || 0)) + (parseFloat(form.other_costs) || 0);
  const [showTelegram, setShowTelegram] = useState(false);
  const [tgConfig, setTgConfig] = useState({
    bot_token: '', chat_id: '', enabled: false,
    alert_price_drop: true, alert_price_drop_pct: 5,
    alert_yield_drop: true, alert_yield_threshold: 4,
    alert_dividend_cut: true, check_interval_minutes: 30,
    bot_token_masked: ''
  });
  const [tgSaving, setTgSaving] = useState(false);
  const [tgTesting, setTgTesting] = useState(false);
  const [tgChecking, setTgChecking] = useState(false);
  const [alertHistory, setAlertHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [transactions, setTransactions] = useState([]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/br/dashboard`, { withCredentials: true });
      setDashboard(res.data);
      setHoldings(res.data.holdings || []);
    } catch (err) {
      if (err.response?.status === 403) navigate('/dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const openTxDetail = async (ticker) => {
    setTxDetailTicker(ticker);
    setTxDetailLoading(true);
    setTxEditingId(null);
    setTxAddMode(false);
    setTxDetailData([]);
    setTxDetailTotal(0);
    try {
      const res = await axios.get(`${API_URL}/api/admin/br/transactions?ticker=${ticker}&skip=0&limit=20`, { withCredentials: true });
      setTxDetailData(res.data.items || []);
      setTxDetailTotal(res.data.total || 0);
    } catch { setTxDetailData([]); }
    finally { setTxDetailLoading(false); }
  };

  const refreshTxDetail = async () => {
    if (!txDetailTicker) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/br/transactions?ticker=${txDetailTicker}&skip=0&limit=${Math.max(txDetailData.length, 20)}`, { withCredentials: true });
      setTxDetailData(res.data.items || []);
      setTxDetailTotal(res.data.total || 0);
    } catch {}
  };

  const loadMoreTx = async () => {
    if (!txDetailTicker || txDetailData.length >= txDetailTotal) return;
    setTxLoadingMore(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/br/transactions?ticker=${txDetailTicker}&skip=${txDetailData.length}&limit=20`, { withCredentials: true });
      setTxDetailData(prev => [...prev, ...(res.data.items || [])]);
      setTxDetailTotal(res.data.total || 0);
    } catch {}
    finally { setTxLoadingMore(false); }
  };

  const handleTxAdd = async () => {
    if (!txAddForm.quantity || !txAddForm.price) { toast.error(t('br.fillRequired')); return; }
    try {
      await axios.post(`${API_URL}/api/admin/br/holdings`, {
        ticker: txDetailTicker,
        asset_type: (holdings.find(x => x.ticker === txDetailTicker) || {}).asset_type || 'fii',
        quantity: parseFloat(txAddForm.quantity),
        avg_price: parseFloat(txAddForm.price),
        transaction_type: txAddForm.transaction_type,
        purchase_date: txAddForm.purchase_date,
        other_costs: parseFloat(txAddForm.other_costs) || 0,
        sector: (holdings.find(x => x.ticker === txDetailTicker) || {}).sector || '',
      }, { withCredentials: true });
      toast.success(t('br.added'));
      setTxAddMode(false);
      setTxAddForm({ transaction_type: 'buy', quantity: '', price: '', other_costs: '', purchase_date: new Date().toISOString().split('T')[0] });
      refreshTxDetail();
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || t('br.addFailed')); }
  };

  const handleTxUpdate = async (txId) => {
    try {
      await axios.put(`${API_URL}/api/admin/br/transactions/${txId}`, txEditForm, { withCredentials: true });
      toast.success(t('br.updated'));
      setTxEditingId(null);
      refreshTxDetail();
      fetchDashboard();
    } catch { toast.error(t('br.updateFailed')); }
  };

  const handleTxDelete = async (txId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/br/transactions/${txId}`, { withCredentials: true });
      toast.success(t('br.deleted'));
      refreshTxDetail();
      fetchDashboard();
    } catch { toast.error(t('br.deleteFailed')); }
  };

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/br/transactions?skip=0&limit=20`, { withCredentials: true });
      setTransactions(res.data.items || res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (showHistory) fetchTransactions();
  }, [showHistory, fetchTransactions]);

  const fetchTelegramConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/br/telegram-config`, { withCredentials: true });
      setTgConfig(prev => ({ ...prev, ...res.data, bot_token: '' }));
    } catch {}
  }, []);

  const fetchAlertHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/br/alert-history`, { withCredentials: true });
      setAlertHistory(res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (showTelegram) {
      fetchTelegramConfig();
      fetchAlertHistory();
    }
  }, [showTelegram, fetchTelegramConfig, fetchAlertHistory]);

  const handleTgSave = async () => {
    setTgSaving(true);
    try {
      const payload = { ...tgConfig };
      if (!payload.bot_token && tgConfig.bot_token_masked) {
        delete payload.bot_token;
      }
      await axios.post(`${API_URL}/api/admin/br/telegram-config`, payload, { withCredentials: true });
      toast.success(t('br.telegram.saved'));
      fetchTelegramConfig();
    } catch {
      toast.error(t('br.telegram.saveFailed'));
    } finally { setTgSaving(false); }
  };

  const handleTgTest = async () => {
    setTgTesting(true);
    try {
      await axios.post(`${API_URL}/api/admin/br/telegram-test`, {}, { withCredentials: true });
      toast.success(t('br.telegram.testSuccess'));
    } catch {
      toast.error(t('br.telegram.testFailed'));
    } finally { setTgTesting(false); }
  };

  const handleTgCheckNow = async () => {
    setTgChecking(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/br/check-alerts`, {}, { withCredentials: true });
      const count = res.data?.alerts_sent || 0;
      if (count > 0) {
        toast.success(`${count} ${t('br.telegram.alertsSent')}`);
      } else {
        toast.info(t('br.telegram.noAlerts'));
      }
      fetchAlertHistory();
    } catch {
      toast.error('Alert check failed');
    } finally { setTgChecking(false); }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Invalidate cache by deleting it
      await axios.get(`${API_URL}/api/admin/br/market-data`, { withCredentials: true });
    } catch {}
    await fetchDashboard();
  };

  const handleAdd = async () => {
    if (!form.ticker || !form.quantity || !form.avg_price) {
      toast.error(t('br.fillRequired'));
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/api/admin/br/holdings`, {
        ticker: form.ticker.toUpperCase(),
        asset_type: form.asset_type,
        quantity: parseFloat(form.quantity),
        avg_price: parseFloat(form.avg_price),
        sector: form.sector,
        notes: form.notes,
        transaction_type: form.transaction_type,
        purchase_date: form.purchase_date,
        other_costs: parseFloat(form.other_costs) || 0,
      }, { withCredentials: true });
      if (res.data?.message === 'merged') {
        toast.success(`${form.ticker.toUpperCase()} ${t('br.merged')} (${t('br.avgPrice')}: R$ ${res.data.avg_price})`);
      } else {
        toast.success(t('br.added'));
      }
      setForm({ ticker: '', asset_type: 'stock', quantity: '1', avg_price: '', sector: '', notes: '', transaction_type: 'buy', purchase_date: new Date().toISOString().split('T')[0], other_costs: '' });
      setTickerQuery('');
      setShowSuggestions(false);
      setB3Result(null);
      setShowAdd(false);
      fetchDashboard();
      if (showHistory) fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('br.addFailed'));
    }
  };

  const handleUpdate = async () => {
    if (!editingItem) return;
    try {
      await axios.put(`${API_URL}/api/admin/br/holdings/${editingItem.id}`, {
        quantity: parseFloat(editingItem.quantity),
        avg_price: parseFloat(editingItem.avg_price),
        sector: editingItem.sector,
        notes: editingItem.notes,
      }, { withCredentials: true });
      toast.success(t('br.updated'));
      setEditingItem(null);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('br.updateFailed'));
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/br/holdings/${id}`, { withCredentials: true });
      toast.success(t('br.deleted'));
      fetchDashboard();
    } catch (err) {
      toast.error(t('br.deleteFailed'));
    }
  };

  const sortedHoldings = [...holdings].sort((a, b) => {
    const av = a[sortField] || 0, bv = b[sortField] || 0;
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'desc' ? <ChevronDown className="h-3 w-3 inline" /> : <ChevronUp className="h-3 w-3 inline" />;
  };

  if (loading && holdings.length === 0) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
    </div>
  );

  const s = dashboard?.summary || {};
  const fx = dashboard?.fx || {};

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-[1600px] mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} className="text-zinc-400 hover:text-white" data-testid="br-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="br-invest-title">
                <svg viewBox="0 0 20 14" className="w-5 h-3.5 shrink-0"><rect width="20" height="14" rx="1" fill="#009739"/><polygon points="10,1.5 18.5,7 10,12.5 1.5,7" fill="#FEDD00"/><circle cx="10" cy="7" r="3" fill="#012169"/></svg>
                {t('br.title')}
              </h1>
              <p className="text-zinc-500 text-sm">{t('br.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleScanB3} disabled={b3Scanning} className="bg-sky-800 text-sky-300 hover:bg-sky-700 rounded-sm" data-testid="br-scan-b3">
              {b3Scanning ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              B3 Sync {masterTickers.length > 0 && <span className="text-sky-400/60 text-xs ml-1">({masterTickers.length})</span>}
            </Button>
            <Button size="sm" onClick={handleRefresh} disabled={refreshing} className="bg-zinc-800 text-white hover:bg-zinc-700 rounded-sm" data-testid="br-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />{t('br.refresh')}
            </Button>
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-500 rounded-sm" data-testid="br-add-btn">
                  <Plus className="h-4 w-4 mr-1.5" />{t('br.addHolding')}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#141414] border-white/10 text-white max-w-lg p-0 gap-0 max-h-[90vh] overflow-hidden">
                <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/5">
                  <DialogTitle className="text-lg">{t('br.addTransaction')}</DialogTitle>
                </DialogHeader>
                <div className="px-5 py-4 space-y-3 overflow-y-auto max-h-[70vh]">
                  {/* Buy / Sell Toggle */}
                  <div className="grid grid-cols-2 gap-0 bg-[#0A0A0A] rounded-lg p-0.5 border border-white/5" data-testid="br-add-tx-type">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, transaction_type: 'buy' })}
                      className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-all ${form.transaction_type === 'buy' ? 'bg-[#1E1E1E] text-emerald-400 shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
                      data-testid="br-add-buy"
                    >
                      <DollarSign className="h-3.5 w-3.5" />{t('br.buy')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, transaction_type: 'sell' })}
                      className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-all ${form.transaction_type === 'sell' ? 'bg-[#1E1E1E] text-red-400 shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
                      data-testid="br-add-sell"
                    >
                      <DollarSign className="h-3.5 w-3.5" />{t('br.sell')}
                    </button>
                  </div>

                  {/* Ticker Input with B3 Search */}
                  <div className="relative">
                    <Label className="text-zinc-400 text-xs">{t('br.ticker')} <span className="text-zinc-600">({t('br.b3Search')})</span></Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={tickerQuery}
                        onChange={e => {
                          const v = e.target.value.toUpperCase();
                          setTickerQuery(v);
                          setForm({ ...form, ticker: v });
                          setShowSuggestions(v.length > 0);
                          setB3Result(null);
                          // Auto B3 search when no suggestions found after typing
                          if (autoSearchTimer) clearTimeout(autoSearchTimer);
                          if (v.length >= 4) {
                            const matches = allTickers.filter(t =>
                              (t.ticker || '').toLowerCase().includes(v.toLowerCase())
                            );
                            if (matches.length === 0) {
                              setAutoSearchTimer(setTimeout(() => searchB3(v), 800));
                            }
                          }
                        }}
                        onFocus={() => tickerQuery.length > 0 && setShowSuggestions(true)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchB3(tickerQuery); } }}
                        placeholder="MXRF11, VALE3..."
                        className="bg-[#0A0A0A] border-white/10 text-white rounded-lg h-10 flex-1 font-mono"
                        autoComplete="off"
                        data-testid="br-add-ticker"
                      />
                      <Button type="button" size="sm" onClick={() => searchB3(tickerQuery)} disabled={b3Searching || tickerQuery.length < 3}
                        className="bg-sky-600 text-white hover:bg-sky-500 rounded-lg h-10 px-3 shrink-0" data-testid="br-b3-search">
                        {b3Searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1" />B3</>}
                      </Button>
                    </div>
                    {showSuggestions && tickerSuggestions.length > 0 && (
                      <div className="absolute z-50 left-0 right-0 mt-1 bg-[#1A1A1A] border border-white/10 rounded-lg shadow-xl max-h-48 overflow-y-auto" data-testid="br-ticker-suggestions">
                        {tickerSuggestions.map(item => (
                          <button key={item.ticker} type="button" onClick={() => selectTicker(item)}
                            className="w-full text-left px-3 py-2 hover:bg-white/[0.06] transition-colors flex items-center gap-2 border-b border-white/5 last:border-0"
                            data-testid={`br-suggest-${item.ticker}`}>
                            <span className="text-white font-mono font-semibold text-sm">{item.ticker}</span>
                            <span className="text-zinc-500 text-xs truncate">{item.name}</span>
                            <span className="ml-auto bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded text-[10px] shrink-0">{(item.type || item.asset_type || '').toUpperCase()}</span>
                          </button>
                        ))}
                        {tickerQuery.length >= 3 && !tickerSuggestions.find(s => s.ticker === tickerQuery) && (
                          <button type="button" onClick={() => searchB3(tickerQuery)}
                            className="w-full text-left px-3 py-2 hover:bg-sky-900/30 transition-colors flex items-center gap-2 text-sky-400 text-xs">
                            <RefreshCw className="h-3 w-3" /> B3{t('br.b3SearchAction')} "{tickerQuery}"
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* B3 Result Info + Auto-detected Type */}
                  {b3Result && (
                    <div className="flex items-center gap-2 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-lg px-3 py-1.5" data-testid="br-b3-result">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <p className="text-white text-xs font-medium truncate flex-1">{b3Result.name}</p>
                      <span className="text-zinc-400 text-[10px] shrink-0">{formatBRL(b3Result.price)} | {b3Result.annual_yield}%</span>
                      <span className="bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0">{form.asset_type.toUpperCase()}</span>
                    </div>
                  )}

                  {/* Asset Type (auto-set, editable) + Sector (auto) */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs">{t('br.assetType')} {b3Result && <span className="text-emerald-400 text-[10px]">({t('br.autoDetected')})</span>}</Label>
                      <Select value={form.asset_type} onValueChange={v => setForm({...form, asset_type: v})}>
                        <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-lg mt-1 h-9" data-testid="br-add-type"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">{ASSET_TYPES.map(a => <SelectItem key={a.value} value={a.value} className="text-zinc-300">{a.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">{t('br.sector')} {b3Result && <span className="text-emerald-400 text-[10px]">({t('br.autoDetected')})</span>}</Label>
                      <Input value={form.sector} readOnly
                        className="bg-[#0A0A0A] border-white/10 text-white rounded-lg mt-1 h-9 cursor-default" data-testid="br-add-sector" />
                    </div>
                  </div>

                  {/* Date + Quantity + Price + Costs */}
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <Label className="text-zinc-400 text-xs">{t('br.purchaseDate')}</Label>
                      <Input type="date" value={form.purchase_date} onChange={e => setForm({...form, purchase_date: e.target.value})}
                        className="bg-[#0A0A0A] border-white/10 text-white rounded-lg mt-1 h-9 [color-scheme:dark]" data-testid="br-add-date" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">{t('br.quantity')}</Label>
                      <Input type="number" min="1" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})}
                        className="bg-[#0A0A0A] border-white/10 text-white rounded-lg mt-1 h-9" data-testid="br-add-qty" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">{t('br.priceR$')}</Label>
                      <Input type="number" step="0.01" value={form.avg_price} onChange={e => setForm({...form, avg_price: e.target.value})} placeholder="0,00"
                        className="bg-[#0A0A0A] border-white/10 text-white rounded-lg mt-1 h-9" data-testid="br-add-price" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">{t('br.otherCosts')}</Label>
                      <Input type="number" step="0.01" value={form.other_costs} onChange={e => setForm({...form, other_costs: e.target.value})} placeholder="0"
                        className="bg-[#0A0A0A] border-white/10 text-white rounded-lg mt-1 h-9" data-testid="br-add-costs" />
                    </div>
                  </div>

                  {/* Total Value Bar */}
                  <div className="flex items-center justify-between bg-[#1E1E1E] rounded-lg px-4 py-2 border border-white/5" data-testid="br-add-total">
                    <span className="text-zinc-300 font-medium text-sm">{t('br.totalValue2')}</span>
                    <span className="text-white font-bold text-lg font-mono">{formatBRL(totalValue)}</span>
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center justify-between px-5 py-4 border-t border-white/5">
                  <Button variant="ghost" onClick={() => setShowAdd(false)} className="text-zinc-400 hover:text-white rounded-lg" data-testid="br-add-cancel">
                    {t('br.cancel')}
                  </Button>
                  <Button onClick={handleAdd} className="bg-zinc-800 text-white hover:bg-zinc-700 rounded-lg px-5" data-testid="br-add-submit">
                    <Plus className="h-4 w-4 mr-1.5" />{t('br.addTransaction')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="portfolio" className="mb-6">
          <TabsList className="bg-[#141414] border border-white/5 p-1 rounded-lg">
            <TabsTrigger value="portfolio" className="data-[state=active]:bg-white/10 rounded-md text-xs px-3 py-1.5" data-testid="br-tab-portfolio">
              <Wallet className="h-3.5 w-3.5 mr-1.5" />{t('br.tabs.portfolio')}
            </TabsTrigger>
            <TabsTrigger value="analysis" className="data-[state=active]:bg-white/10 rounded-md text-xs px-3 py-1.5" data-testid="br-tab-analysis">
              <PieChartIcon className="h-3.5 w-3.5 mr-1.5" />{t('br.tabs.analysis')}
            </TabsTrigger>
            <TabsTrigger value="dividends" className="data-[state=active]:bg-white/10 rounded-md text-xs px-3 py-1.5" data-testid="br-tab-dividends">
              <Coins className="h-3.5 w-3.5 mr-1.5" />{t('br.tabs.dividends')}
            </TabsTrigger>
            <TabsTrigger value="screening" className="data-[state=active]:bg-white/10 rounded-md text-xs px-3 py-1.5" data-testid="br-tab-screening">
              <Star className="h-3.5 w-3.5 mr-1.5" />{t('br.tabs.screening')}
            </TabsTrigger>
          </TabsList>

          {/* ====== PORTFOLIO TAB ====== */}
          <TabsContent value="portfolio" className="mt-4 space-y-4">

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <Card className="bg-[#141414] border-white/5">
            <CardContent className="p-3">
              <p className="text-zinc-500 text-xs mb-1">{t('br.totalInvested')}</p>
              <p className="text-amber-400 font-bold text-lg font-mono" data-testid="br-total-invested">{formatBRL(s.total_invested_brl)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#141414] border-white/5">
            <CardContent className="p-3">
              <p className="text-zinc-500 text-xs mb-1">{t('br.totalValue')} (BRL)</p>
              <p className="text-white font-bold text-lg font-mono" data-testid="br-total-brl">{formatBRL(s.total_current_brl)}</p>
              <PnlBadge value={s.total_pnl_pct} />
            </CardContent>
          </Card>
          <Card className={`border-white/5 ${(s.total_pnl_brl || 0) >= 0 ? 'bg-emerald-500/[0.06]' : 'bg-red-500/[0.06]'}`}>
            <CardContent className="p-3">
              <p className="text-zinc-500 text-xs mb-1">{t('br.pnlAmount')}</p>
              <p className={`font-bold text-lg font-mono ${(s.total_pnl_brl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="br-total-pnl">
                {(s.total_pnl_brl || 0) >= 0 ? '+' : ''}{formatBRL(s.total_pnl_brl)}
              </p>
              <p className={`text-xs font-mono ${(s.total_pnl_pct || 0) >= 0 ? 'text-emerald-500/60' : 'text-red-500/60'}`}>{formatPct(s.total_pnl_pct)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#141414] border-white/5">
            <CardContent className="p-3">
              <p className="text-zinc-500 text-xs mb-1"><Coins className="h-3.5 w-3.5 inline mr-1" />{t('br.monthlyDiv')}</p>
              <p className="text-emerald-400 font-bold text-lg font-mono" data-testid="br-monthly-div">{formatBRL(s.total_monthly_div)}</p>
              <p className="text-zinc-600 text-xs">{formatJPY((s.total_monthly_div || 0) * (fx.BRL_JPY || 0))}/mo</p>
            </CardContent>
          </Card>
          <Card className="bg-[#141414] border-white/5">
            <CardContent className="p-3">
              <p className="text-zinc-500 text-xs mb-1"><BarChart3 className="h-3.5 w-3.5 inline mr-1" />{t('br.annualDiv')}</p>
              <p className="text-emerald-400 font-bold text-lg font-mono" data-testid="br-annual-div">{formatBRL(s.total_annual_div)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#141414] border-white/5">
            <CardContent className="p-3">
              <p className="text-zinc-500 text-xs mb-1"><Shield className="h-3.5 w-3.5 inline mr-1" />{t('br.portfolioYield')}</p>
              <p className="text-lg font-mono" data-testid="br-portfolio-yield"><YieldBadge value={s.portfolio_yield} /></p>
            </CardContent>
          </Card>
          <Card className="bg-[#141414] border-white/5">
            <CardContent className="p-3">
              <p className="text-zinc-500 text-xs mb-1"><DollarSign className="h-3.5 w-3.5 inline mr-1" />{t('br.fxRates')}</p>
              <p className="text-white text-sm font-mono">BRL/JPY: {fx.BRL_JPY || '-'}</p>
              <p className="text-zinc-400 text-xs font-mono">USD/BRL: {fx.USD_BRL || '-'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Telegram Alert Settings */}
        <Card className="bg-[#141414] border-white/5 mb-6">
          <button
            onClick={() => setShowTelegram(!showTelegram)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.02] transition-colors"
            data-testid="br-telegram-toggle"
          >
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-sky-400" />
              <span className="text-white font-medium text-sm">{t('br.telegram.title')}</span>
              {tgConfig.enabled && tgConfig.bot_token_masked && (
                <span className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded text-xs">ON</span>
              )}
            </div>
            {showTelegram ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
          </button>

          {showTelegram && (
            <CardContent className="pt-0 pb-4 px-4 space-y-4">
              {/* Bot Token & Chat ID */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs flex items-center gap-1">
                    {t('br.telegram.botToken')}
                    <span className="text-zinc-600 text-[10px]">({t('br.telegram.howTo')})</span>
                  </Label>
                  <Input
                    value={tgConfig.bot_token}
                    onChange={e => setTgConfig({ ...tgConfig, bot_token: e.target.value })}
                    placeholder={tgConfig.bot_token_masked || '123456:ABC-DEF...'}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 font-mono text-xs"
                    data-testid="br-tg-token"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs flex items-center gap-1">
                    {t('br.telegram.chatId')}
                    <span className="text-zinc-600 text-[10px]">({t('br.telegram.howToChatId')})</span>
                  </Label>
                  <Input
                    value={tgConfig.chat_id}
                    onChange={e => setTgConfig({ ...tgConfig, chat_id: e.target.value })}
                    placeholder="-100..."
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 font-mono text-xs"
                    data-testid="br-tg-chatid"
                  />
                </div>
              </div>

              {/* Alert Conditions */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex items-center gap-2 bg-[#0A0A0A] p-2.5 rounded border border-white/5">
                  <input type="checkbox" checked={tgConfig.alert_price_drop}
                    onChange={e => setTgConfig({ ...tgConfig, alert_price_drop: e.target.checked })}
                    className="accent-sky-500" data-testid="br-tg-price-drop" />
                  <div className="flex-1">
                    <p className="text-zinc-300 text-xs">{t('br.telegram.priceDrop')}</p>
                    <Input type="number" step="0.5" value={tgConfig.alert_price_drop_pct}
                      onChange={e => setTgConfig({ ...tgConfig, alert_price_drop_pct: parseFloat(e.target.value) || 5 })}
                      className="bg-transparent border-white/10 text-white rounded-sm mt-1 h-7 text-xs w-20 font-mono"
                      data-testid="br-tg-price-drop-pct" />
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-[#0A0A0A] p-2.5 rounded border border-white/5">
                  <input type="checkbox" checked={tgConfig.alert_yield_drop}
                    onChange={e => setTgConfig({ ...tgConfig, alert_yield_drop: e.target.checked })}
                    className="accent-sky-500" data-testid="br-tg-yield-drop" />
                  <div className="flex-1">
                    <p className="text-zinc-300 text-xs">{t('br.telegram.yieldDrop')}</p>
                    <Input type="number" step="0.5" value={tgConfig.alert_yield_threshold}
                      onChange={e => setTgConfig({ ...tgConfig, alert_yield_threshold: parseFloat(e.target.value) || 4 })}
                      className="bg-transparent border-white/10 text-white rounded-sm mt-1 h-7 text-xs w-20 font-mono"
                      data-testid="br-tg-yield-threshold" />
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-[#0A0A0A] p-2.5 rounded border border-white/5">
                  <input type="checkbox" checked={tgConfig.alert_dividend_cut}
                    onChange={e => setTgConfig({ ...tgConfig, alert_dividend_cut: e.target.checked })}
                    className="accent-sky-500" data-testid="br-tg-dividend-cut" />
                  <div className="flex-1">
                    <p className="text-zinc-300 text-xs">{t('br.telegram.dividendCut')}</p>
                  </div>
                </div>
              </div>

              {/* Interval + Enable + Actions */}
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">{t('br.telegram.checkInterval')}</Label>
                  <Input type="number" min="10" value={tgConfig.check_interval_minutes}
                    onChange={e => setTgConfig({ ...tgConfig, check_interval_minutes: parseInt(e.target.value) || 30 })}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 h-8 text-xs w-20 font-mono"
                    data-testid="br-tg-interval" />
                </div>
                <div className="flex items-center gap-2 bg-[#0A0A0A] px-3 py-1.5 rounded border border-white/5">
                  <input type="checkbox" checked={tgConfig.enabled}
                    onChange={e => setTgConfig({ ...tgConfig, enabled: e.target.checked })}
                    className="accent-emerald-500" data-testid="br-tg-enabled" />
                  <span className="text-zinc-300 text-xs font-medium">{t('br.telegram.enabled')}</span>
                </div>
                <Button size="sm" onClick={handleTgSave} disabled={tgSaving}
                  className="bg-sky-600 text-white hover:bg-sky-500 rounded-sm h-8 text-xs" data-testid="br-tg-save">
                  {tgSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Settings className="h-3 w-3 mr-1" />}
                  {t('common.save')}
                </Button>
                <Button size="sm" variant="outline" onClick={handleTgTest} disabled={tgTesting}
                  className="border-sky-600/50 text-sky-400 hover:bg-sky-900/30 rounded-sm h-8 text-xs" data-testid="br-tg-test">
                  {tgTesting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                  {t('br.telegram.testSend')}
                </Button>
                <Button size="sm" variant="outline" onClick={handleTgCheckNow} disabled={tgChecking}
                  className="border-amber-600/50 text-amber-400 hover:bg-amber-900/30 rounded-sm h-8 text-xs" data-testid="br-tg-check-now">
                  {tgChecking ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Bell className="h-3 w-3 mr-1" />}
                  {t('br.telegram.checkNow')}
                </Button>
              </div>

              {/* Alert History */}
              {alertHistory.length > 0 && (
                <div>
                  <p className="text-zinc-500 text-xs font-medium mb-2">{t('br.telegram.history')}</p>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {alertHistory.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 bg-[#0A0A0A] px-3 py-1.5 rounded text-xs border border-white/5">
                        {a.sent ? <CheckCircle2 className="h-3 w-3 text-emerald-400 shrink-0" /> : <XCircle className="h-3 w-3 text-red-400 shrink-0" />}
                        <span className="text-white font-mono font-semibold">{a.ticker}</span>
                        <span className="text-zinc-500">{a.reasons?.join(', ')}</span>
                        <span className="text-zinc-600 ml-auto">{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Holdings Table */}
        <Card className="bg-[#141414] border-white/5">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed" data-testid="br-holdings-table">
                <colgroup>
                  <col style={{width:'12%'}} />
                  <col style={{width:'6%'}} />
                  <col style={{width:'6%'}} />
                  <col style={{width:'9%'}} />
                  <col style={{width:'10%'}} />
                  <col style={{width:'10%'}} />
                  <col style={{width:'10%'}} />
                  <col style={{width:'8%'}} />
                  <col style={{width:'7%'}} />
                  <col style={{width:'9%'}} />
                  <col style={{width:'6%'}} />
                  <col style={{width:'7%'}} />
                </colgroup>
                <thead>
                  <tr className="border-b border-white/5 text-zinc-500 text-xs">
                    <th className="text-left py-3 px-3">{t('br.ticker')}</th>
                    <th className="text-left py-3 px-2">{t('br.type')}</th>
                    <th className="text-right py-3 px-2 cursor-pointer hover:text-white" onClick={() => toggleSort('quantity')}>{t('br.qty')} <SortIcon field="quantity" /></th>
                    <th className="text-right py-3 px-2">{t('br.avgPrice')}</th>
                    <th className="text-right py-3 px-2 cursor-pointer hover:text-white text-amber-400/70" onClick={() => toggleSort('invested_brl')}>{t('br.totalInvested')} <SortIcon field="invested_brl" /></th>
                    <th className="text-right py-3 px-2 cursor-pointer hover:text-white" onClick={() => toggleSort('current_price')}>{t('br.currentPrice')} <SortIcon field="current_price" /></th>
                    <th className="text-right py-3 px-2 cursor-pointer hover:text-white" onClick={() => toggleSort('current_brl')}>{t('br.valueBRL')} <SortIcon field="current_brl" /></th>
                    <th className="text-right py-3 px-2 cursor-pointer hover:text-white" onClick={() => toggleSort('pnl_pct')}>{t('br.pnl')} <SortIcon field="pnl_pct" /></th>
                    <th className="text-right py-3 px-2 cursor-pointer hover:text-white" onClick={() => toggleSort('annual_yield')}><span className="text-emerald-400">Yield</span> <SortIcon field="annual_yield" /></th>
                    <th className="text-right py-3 px-2 cursor-pointer hover:text-white" onClick={() => toggleSort('total_monthly_div')}>{t('br.monthlyDiv')} <SortIcon field="total_monthly_div" /></th>
                    <th className="text-center py-3 px-2">P/VP</th>
                    <th className="text-center py-3 px-2">{t('br.risk')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedHoldings.map(h => (
                    <tr key={h.id} onClick={() => openTxDetail(h.ticker)} className="border-b border-white/5 hover:bg-emerald-500/[0.30] transition-colors cursor-pointer" data-testid={`br-row-${h.ticker}`}>
                      <td className="py-2.5 px-3">
                        <span className="text-white font-semibold" data-testid={`br-detail-${h.ticker}`}>{h.ticker}</span>
                        {h.sector && <span className="text-zinc-600 text-xs ml-1.5">{h.sector}</span>}
                      </td>
                      <td className="py-2.5 px-2"><span className="bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded text-xs">{h.asset_type.toUpperCase()}</span></td>
                      <td className="py-2.5 px-2 text-right text-zinc-300 font-mono">{h.quantity}</td>
                      <td className="py-2.5 px-2 text-right text-zinc-500 font-mono">{formatBRL(h.avg_price)}</td>
                      <td className="py-2.5 px-2 text-right text-amber-400/80 font-mono">{formatBRL(h.invested_brl)}</td>
                      <td className="py-2.5 px-2 text-right font-mono">
                        <span className="text-white">{formatBRL(h.current_price)}</span>
                        <span className={`block text-xs ${h.change_pct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatPct(h.change_pct)}</span>
                      </td>
                      <td className="py-2.5 px-2 text-right text-white font-mono font-medium">{formatBRL(h.current_brl)}</td>
                      <td className="py-2.5 px-2 text-right"><PnlBadge value={h.pnl_pct} /></td>
                      <td className="py-2.5 px-2 text-right"><YieldBadge value={h.annual_yield} /></td>
                      <td className="py-2.5 px-2 text-right text-emerald-300 font-mono text-xs">{formatBRL(h.total_monthly_div)}</td>
                      <td className="py-2.5 px-2 text-center font-mono text-xs">
                        {h.p_vp ? (
                          <span className={h.p_vp < 1 ? 'text-emerald-400 font-bold' : h.p_vp > 1.2 ? 'text-red-400' : 'text-zinc-300'}>{h.p_vp}</span>
                        ) : '-'}
                      </td>
                      <td className="py-2.5 px-2 text-center"><RiskBadge score={h.risk_score} factors={h.risk_factors} /></td>
                    </tr>
                  ))}
                  {sortedHoldings.length === 0 && (
                    <tr><td colSpan={12} className="text-center py-10 text-zinc-600">{t('br.noHoldings')}</td></tr>
                  )}
                </tbody>
                {sortedHoldings.length > 0 && (
                  <tfoot>
                    <tr className="border-t border-white/10 bg-white/[0.02] text-sm font-semibold">
                      <td className="py-3 px-3 text-white">{t('br.total')} ({s.holdings_count})</td>
                      <td></td>
                      <td className="py-3 px-2 text-right text-zinc-400 font-mono text-xs">{holdings.reduce((s, h) => s + (h.quantity || 0), 0)}</td>
                      <td></td>
                      <td className="py-3 px-2 text-right text-amber-400 font-mono" data-testid="br-footer-invested">{formatBRL(s.total_invested_brl)}</td>
                      <td></td>
                      <td className="py-3 px-2 text-right text-white font-mono">{formatBRL(s.total_current_brl)}</td>
                      <td className="py-3 px-2 text-right"><PnlBadge value={s.total_pnl_pct} /></td>
                      <td className="py-3 px-2 text-right"><YieldBadge value={s.portfolio_yield} /></td>
                      <td className="py-3 px-2 text-right text-emerald-300 font-mono text-xs">{formatBRL(s.total_monthly_div)}</td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card className="bg-[#141414] border-white/5 mt-4">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.02] transition-colors"
            data-testid="br-history-toggle"
          >
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-sky-400" />
              <span className="text-white font-medium text-sm">{t('br.transactionHistory')}</span>
              {transactions.length > 0 && (
                <span className="bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded text-xs">{transactions.length}</span>
              )}
            </div>
            {showHistory ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
          </button>
          {showHistory && (
            <CardContent className="pt-0 pb-3 px-0">
              {transactions.length === 0 ? (
                <p className="text-zinc-600 text-sm text-center py-6">{t('br.noTransactions')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="br-transactions-table">
                    <thead>
                      <tr className="border-b border-white/5 text-zinc-500 text-xs">
                        <th className="text-left py-2 px-4">{t('br.date')}</th>
                        <th className="text-left py-2 px-2">{t('br.txType')}</th>
                        <th className="text-left py-2 px-2">{t('br.ticker')}</th>
                        <th className="text-right py-2 px-2">{t('br.quantity')}</th>
                        <th className="text-right py-2 px-2">{t('br.priceR$')}</th>
                        <th className="text-right py-2 px-2">{t('br.otherCosts')}</th>
                        <th className="text-right py-2 px-4">{t('br.totalValue2')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map(tx => (
                        <tr key={tx.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`br-tx-${tx.id}`}>
                          <td className="py-2 px-4 text-zinc-400 text-xs font-mono">{tx.purchase_date || tx.created_at?.split('T')[0]}</td>
                          <td className="py-2 px-2">
                            {tx.transaction_type === 'buy' ? (
                              <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-medium"><ArrowDownLeft className="h-3 w-3" />{t('br.buy')}</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-400 text-xs font-medium"><ArrowUpRight className="h-3 w-3" />{t('br.sell')}</span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-white font-mono font-semibold">{tx.ticker}</td>
                          <td className="py-2 px-2 text-right text-zinc-300 font-mono">{tx.quantity}</td>
                          <td className="py-2 px-2 text-right text-zinc-300 font-mono">{formatBRL(tx.price)}</td>
                          <td className="py-2 px-2 text-right text-zinc-500 font-mono text-xs">{tx.other_costs > 0 ? formatBRL(tx.other_costs) : '-'}</td>
                          <td className="py-2 px-4 text-right text-white font-mono font-medium">{formatBRL(tx.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Updated timestamp */}
        {dashboard?.updated_at && (
          <p className="text-zinc-600 text-xs mt-3 text-right" data-testid="br-updated-at">
            {t('br.lastUpdate')}: {new Date(dashboard.updated_at).toLocaleString()}
          </p>
        )}

          </TabsContent>

          {/* ====== ANALYSIS TAB ====== */}
          <TabsContent value="analysis" className="mt-4 space-y-4">
            <InvestorProfilePanel />
            <PortfolioCharts />
          </TabsContent>

          {/* ====== DIVIDENDS TAB ====== */}
          <TabsContent value="dividends" className="mt-4">
            <DividendPanel />
          </TabsContent>

          {/* ====== SCREENING TAB ====== */}
          <TabsContent value="screening" className="mt-4">
            <ScreeningPanel />
          </TabsContent>
        </Tabs>
      </div>

      {/* Transaction Detail Popup - A+B+C Enhanced */}
      <Dialog open={!!txDetailTicker} onOpenChange={(o) => !o && setTxDetailTicker(null)}>
        <DialogContent className="bg-[#181818] border-2 border-white/20 text-white max-w-2xl p-0 gap-0 max-h-[85vh] overflow-hidden shadow-[0_0_80px_rgba(255,255,255,0.06)] ring-1 ring-white/15">
          {(() => {
            const h = holdings.find(x => x.ticker === txDetailTicker) || {};
            const totalBuy = txDetailData.reduce((s, tx) => s + (tx.transaction_type === 'buy' ? tx.quantity : 0), 0);
            const totalSell = txDetailData.reduce((s, tx) => s + (tx.transaction_type === 'sell' ? tx.quantity : 0), 0);
            const totalCost = txDetailData.reduce((s, tx) => s + (tx.total || 0), 0);
            const pnlPct = h.pnl_pct || 0;
            // Sort by purchase_date ascending for correct chronological order
            const sortedTxs = [...txDetailData].sort((a, b) => {
              const da = a.purchase_date || a.created_at?.split('T')[0] || '';
              const db = b.purchase_date || b.created_at?.split('T')[0] || '';
              if (da !== db) return da.localeCompare(db);
              return (a.created_at || '').localeCompare(b.created_at || '');
            });
            const chartData = [];
            let cumQty = 0;
            sortedTxs.forEach(tx => {
              cumQty += tx.transaction_type === 'buy' ? tx.quantity : -tx.quantity;
              chartData.push({ date: (tx.purchase_date || tx.created_at?.split('T')[0] || '').slice(5), fullDate: tx.purchase_date || tx.created_at?.split('T')[0], qty: cumQty, price: tx.price });
            });
            return (<>
              <div className="px-5 pt-5 pb-4 border-b border-white/5 bg-gradient-to-b from-white/[0.03] to-transparent">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl font-bold tracking-tight">{txDetailTicker}</span>
                      <span className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded text-[10px] font-bold">{(h.asset_type || 'fii').toUpperCase()}</span>
                    </div>
                    <p className="text-zinc-500 text-xs">{h.sector || ''}</p>
                    <div className="flex gap-1.5 mt-1.5">
                      <Button size="sm" onClick={() => { setTxAddForm(f => ({...f, transaction_type: 'buy'})); setTxAddMode(true); }} className="bg-emerald-600 text-white hover:bg-emerald-500 rounded h-6 text-[10px] px-2.5" data-testid="br-tx-add-btn">
                        <ArrowDownLeft className="h-3 w-3 mr-1" />{t('br.buy')}
                      </Button>
                      <Button size="sm" onClick={() => { setTxAddForm(f => ({...f, transaction_type: 'sell'})); setTxAddMode(true); }} className="bg-red-600 text-white hover:bg-red-500 rounded h-6 text-[10px] px-2.5" data-testid="br-tx-sell-btn">
                        <ArrowUpRight className="h-3 w-3 mr-1" />{t('br.sell')}
                      </Button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold text-lg font-mono">{formatBRL(h.current_price)}</p>
                    <span className={`text-xs font-mono ${(h.change_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatPct(h.change_pct)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  <div className="bg-white/[0.04] rounded-lg px-3 py-2">
                    <p className="text-zinc-500 text-[10px]">{t('br.quantity')}</p>
                    <p className="text-white font-mono font-bold text-sm">{h.quantity || 0}</p>
                  </div>
                  <div className="bg-white/[0.04] rounded-lg px-3 py-2">
                    <p className="text-zinc-500 text-[10px]">{t('br.avgPrice')}</p>
                    <p className="text-white font-mono font-bold text-sm">{formatBRL(h.avg_price)}</p>
                  </div>
                  <div className="bg-amber-500/[0.08] rounded-lg px-3 py-2">
                    <p className="text-zinc-500 text-[10px]">{t('br.totalInvested')}</p>
                    <p className="text-amber-400 font-mono font-bold text-sm">{formatBRL(h.invested_brl)}</p>
                  </div>
                  <div className="bg-white/[0.04] rounded-lg px-3 py-2">
                    <p className="text-zinc-500 text-[10px]">{t('br.valueBRL')}</p>
                    <p className="text-white font-mono font-bold text-sm">{formatBRL(h.current_brl)}</p>
                  </div>
                  <div className={`rounded-lg px-3 py-2 ${pnlPct >= 0 ? 'bg-emerald-500/[0.08]' : 'bg-red-500/[0.08]'}`}>
                    <p className="text-zinc-500 text-[10px]">{t('br.pnl')}</p>
                    <p className={`font-mono font-bold text-sm ${pnlPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatPct(pnlPct)}</p>
                  </div>
                </div>
                {h.annual_yield > 0 && (
                  <div className="flex items-center gap-4 mt-2 text-xs">
                    <span className="text-zinc-500">Yield: <span className="text-emerald-400 font-mono font-bold">{h.annual_yield?.toFixed(2)}%</span></span>
                    <span className="text-zinc-500">{t('br.monthlyDiv')}: <span className="text-emerald-300 font-mono">{formatBRL(h.total_monthly_div)}</span></span>
                    {h.p_vp && <span className="text-zinc-500">P/VP: <span className={`font-mono ${h.p_vp < 1 ? 'text-emerald-400' : 'text-zinc-300'}`}>{h.p_vp}</span></span>}
                  </div>
                )}
              </div>
              <div className="overflow-y-auto max-h-[55vh]">
                {txDetailLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>
                ) : txDetailData.length === 0 ? (
                  <p className="text-zinc-600 text-sm text-center py-8">{t('br.noTransactions')}</p>
                ) : (<>
                  {chartData.length > 1 && (
                    <div className="px-5 pt-4 pb-2">
                      <p className="text-zinc-500 text-[10px] font-medium mb-2">{t('br.txTimeline')}</p>
                      <ResponsiveContainer width="100%" height={100}>
                        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                          <defs><linearGradient id="txGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient></defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                          <XAxis dataKey="date" tick={{ fill: '#555', fontSize: 9 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: '#555', fontSize: 9 }} axisLine={false} tickLine={false} width={30} />
                          <Tooltip content={({ active, payload }) => active && payload?.length ? (
                            <div className="bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-xs">
                              <p className="text-zinc-400">{payload[0].payload?.fullDate}</p>
                              <p className="text-white font-mono">{payload[0].payload?.qty} {t('br.qty')} | {formatBRL(payload[0].payload?.price)}</p>
                            </div>
                          ) : null} />
                          <Area type="stepAfter" dataKey="qty" stroke="#10b981" fill="url(#txGrad)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <div className="px-2">
                    <table className="w-full text-sm table-fixed" data-testid="br-tx-detail-table">
                      <colgroup>
                        <col style={{width:'14%'}} />
                        <col style={{width:'11%'}} />
                        <col style={{width:'9%'}} />
                        <col style={{width:'14%'}} />
                        <col style={{width:'12%'}} />
                        <col style={{width:'16%'}} />
                        <col style={{width:'8%'}} />
                        <col style={{width:'16%'}} />
                      </colgroup>
                      <thead><tr className="border-b border-white/5 text-zinc-500 text-[10px]">
                        <th className="text-left py-2 px-3">{t('br.date')}</th>
                        <th className="text-left py-2 px-2">{t('br.txType')}</th>
                        <th className="text-right py-2 px-2">{t('br.quantity')}</th>
                        <th className="text-right py-2 px-2">{t('br.priceR$')}</th>
                        <th className="text-right py-2 px-2">{t('br.otherCosts')}</th>
                        <th className="text-right py-2 px-2">{t('br.totalValue2')}</th>
                        <th className="text-right py-2 px-2">{t('br.cumQty')}</th>
                        <th className="text-center py-2 px-2"></th>
                      </tr></thead>
                    </table>
                    <div className="max-h-[175px] overflow-y-auto" data-testid="br-tx-scroll">
                      <table className="w-full text-sm table-fixed">
                        <colgroup>
                          <col style={{width:'14%'}} />
                          <col style={{width:'11%'}} />
                          <col style={{width:'9%'}} />
                          <col style={{width:'14%'}} />
                          <col style={{width:'12%'}} />
                          <col style={{width:'16%'}} />
                          <col style={{width:'8%'}} />
                          <col style={{width:'16%'}} />
                        </colgroup>
                        <tbody>{(() => { let cum = 0; return sortedTxs.map(tx => { cum += tx.transaction_type === 'buy' ? tx.quantity : -tx.quantity; const isBuy = tx.transaction_type === 'buy'; const isEditing = txEditingId === tx.id; return (
                        <tr key={tx.id} className={`border-b border-white/5 ${isBuy ? 'bg-emerald-500/[0.02] hover:bg-emerald-500/[0.05]' : 'bg-red-500/[0.02] hover:bg-red-500/[0.05]'} transition-colors`}>
                          {isEditing ? (<>
                            <td className="py-1 px-2"><Input type="date" value={txEditForm.purchase_date || ''} onChange={e => setTxEditForm({...txEditForm, purchase_date: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white h-7 text-xs rounded [color-scheme:dark]" /></td>
                            <td className="py-1 px-2">
                              <select value={txEditForm.transaction_type || 'buy'} onChange={e => setTxEditForm({...txEditForm, transaction_type: e.target.value})} className="bg-[#0A0A0A] border border-white/10 text-white h-7 text-xs rounded px-1">
                                <option value="buy">{t('br.buy')}</option><option value="sell">{t('br.sell')}</option>
                              </select>
                            </td>
                            <td className="py-1 px-1"><Input type="number" value={txEditForm.quantity || ''} onChange={e => setTxEditForm({...txEditForm, quantity: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white h-7 text-xs rounded w-full text-right" /></td>
                            <td className="py-1 px-1"><Input type="number" step="0.01" value={txEditForm.price || ''} onChange={e => setTxEditForm({...txEditForm, price: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white h-7 text-xs rounded w-full text-right" /></td>
                            <td className="py-1 px-1"><Input type="number" step="0.01" value={txEditForm.other_costs || ''} onChange={e => setTxEditForm({...txEditForm, other_costs: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white h-7 text-xs rounded w-full text-right" /></td>
                            <td colSpan={2} className="py-1 px-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => handleTxUpdate(tx.id)} className="text-emerald-400 hover:text-emerald-300 p-1" data-testid="br-tx-save"><CheckCircle2 className="h-4 w-4" /></button>
                                <button onClick={() => setTxEditingId(null)} className="text-zinc-500 hover:text-white p-1"><XCircle className="h-4 w-4" /></button>
                              </div>
                            </td>
                          </>) : (<>
                            <td className="py-2 px-3 text-zinc-400 font-mono text-xs">{tx.purchase_date || tx.created_at?.split('T')[0]}</td>
                            <td className="py-2 px-2">{isBuy ? <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-medium"><ArrowDownLeft className="h-3 w-3" />{t('br.buy')}</span> : <span className="inline-flex items-center gap-1 text-red-400 text-xs font-medium"><ArrowUpRight className="h-3 w-3" />{t('br.sell')}</span>}</td>
                            <td className="py-2 px-2 text-right text-zinc-300 font-mono text-xs">{tx.quantity}</td>
                            <td className="py-2 px-2 text-right text-zinc-300 font-mono text-xs">{formatBRL(tx.price)}</td>
                            <td className="py-2 px-2 text-right text-zinc-600 font-mono text-xs">{tx.other_costs > 0 ? formatBRL(tx.other_costs) : '-'}</td>
                            <td className="py-2 px-2 text-right text-white font-mono text-xs font-medium">{formatBRL(tx.total)}</td>
                            <td className="py-2 px-2 text-right font-mono text-xs text-sky-400 font-bold">{cum}</td>
                          </>)}
                          {!isEditing && (
                            <td className="py-2 px-2 text-center">
                              <div className="flex items-center justify-center gap-0.5">
                                <button onClick={() => { setTxEditingId(tx.id); setTxEditForm({ purchase_date: tx.purchase_date, transaction_type: tx.transaction_type, quantity: tx.quantity, price: tx.price, other_costs: tx.other_costs || 0 }); }}
                                  className="text-zinc-600 hover:text-sky-400 p-1" data-testid={`br-tx-edit-${tx.id}`}><Pencil className="h-3 w-3" /></button>
                                <button onClick={() => handleTxDelete(tx.id)}
                                  className="text-zinc-600 hover:text-red-400 p-1" data-testid={`br-tx-del-${tx.id}`}><Trash2 className="h-3 w-3" /></button>
                              </div>
                            </td>
                          )}
                        </tr>); }); })()}</tbody>
                      </table>
                    </div>
                    {txDetailData.length < txDetailTotal && (
                      <button onClick={loadMoreTx} disabled={txLoadingMore}
                        className="w-full py-2 text-xs text-sky-400 hover:text-sky-300 hover:bg-white/[0.02] transition-colors flex items-center justify-center gap-1.5 border-t border-white/5"
                        data-testid="br-tx-load-more">
                        {txLoadingMore ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronDown className="h-3 w-3" />}
                        {t('br.loadMore')} ({txDetailData.length}/{txDetailTotal})
                      </button>
                    )}
                    <table className="w-full text-sm table-fixed">
                      <colgroup>
                        <col style={{width:'14%'}} />
                        <col style={{width:'11%'}} />
                        <col style={{width:'9%'}} />
                        <col style={{width:'14%'}} />
                        <col style={{width:'12%'}} />
                        <col style={{width:'16%'}} />
                        <col style={{width:'8%'}} />
                        <col style={{width:'16%'}} />
                      </colgroup>
                      <tfoot><tr className="border-t border-white/10 bg-white/[0.03]">
                        <td colSpan={2} className="py-2.5 px-3 text-white font-medium text-xs">{t('br.total')} ({txDetailTotal})</td>
                        <td className="py-2.5 px-2 text-right text-white font-mono text-xs font-bold">{totalBuy}{totalSell > 0 && <span className="text-red-400">-{totalSell}</span>}</td>
                        <td></td>
                        <td></td>
                        <td className="py-2.5 px-2 text-right text-amber-400 font-mono text-xs font-bold">{formatBRL(totalCost)}</td>
                        <td className="py-2.5 px-2 text-right text-sky-400 font-mono text-xs font-bold">{h.quantity || 0}</td>
                        <td></td>
                      </tr></tfoot>
                    </table>
                  </div>

                  {/* Add transaction inline */}
                  {txAddMode ? (
                    <div className="mx-4 my-3 p-3 bg-[#0A0A0A] rounded-lg border border-emerald-500/20">
                      <div className="grid grid-cols-5 gap-2 items-end">
                        <div>
                          <Label className="text-zinc-500 text-[10px]">{t('br.txType')}</Label>
                          <select value={txAddForm.transaction_type} onChange={e => setTxAddForm({...txAddForm, transaction_type: e.target.value})}
                            className="w-full bg-[#111] border border-white/10 text-white h-8 text-xs rounded px-2 mt-1">
                            <option value="buy">{t('br.buy')}</option><option value="sell">{t('br.sell')}</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-[10px]">{t('br.quantity')}</Label>
                          <Input type="number" value={txAddForm.quantity} onChange={e => setTxAddForm({...txAddForm, quantity: e.target.value})}
                            className="bg-[#111] border-white/10 text-white h-8 text-xs rounded mt-1" data-testid="br-tx-add-qty" />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-[10px]">{t('br.priceR$')}</Label>
                          <Input type="number" step="0.01" value={txAddForm.price} onChange={e => setTxAddForm({...txAddForm, price: e.target.value})}
                            className="bg-[#111] border-white/10 text-white h-8 text-xs rounded mt-1" data-testid="br-tx-add-price" />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-[10px]">{t('br.purchaseDate')}</Label>
                          <Input type="date" value={txAddForm.purchase_date} onChange={e => setTxAddForm({...txAddForm, purchase_date: e.target.value})}
                            className="bg-[#111] border-white/10 text-white h-8 text-xs rounded mt-1 [color-scheme:dark]" />
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" onClick={handleTxAdd} className="bg-emerald-600 text-white hover:bg-emerald-500 h-8 text-xs rounded flex-1" data-testid="br-tx-add-submit">
                            <Plus className="h-3 w-3 mr-1" />{t('common.save')}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setTxAddMode(false)} className="text-zinc-500 h-8 text-xs rounded">
                            <XCircle className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>)}
              </div>
            </>);
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BRInvestPage;
